'use client';

import {
  type Dispatch,
  type HTMLAttributes,
  type HTMLProps,
  type ReactElement,
  type ReactNode,
  type Ref,
  type RefObject,
  type SetStateAction,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import classnames from 'classnames';

import type { BaseComponentProps } from '../base-component.types';
import { Button } from '../button';
import type { PopoverProps, PopoverTriggerProps } from './Popover.types';

import styles from './Popover.module.scss';

export function usePopover({
  initialOpen = false,
  openAnimation,
  closeAnimation,
  placement = 'bottom',
  strategy,
  modal,
  matchReferenceWidth = false,
  dismissOnAncestorScroll = false,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
}: PopoverProps = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    strategy,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(15),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'end',
        padding: 15,
      }),
      shift({ padding: 15 }),
      ...(matchReferenceWidth
        ? [
            size({
              apply({ rects, elements }) {
                Object.assign(elements.floating.style, {
                  width: `${rects.reference.width}px`,
                });
              },
            }),
          ]
        : []),
    ],
  });

  const context = data.context;

  const click = useClick(context, {
    enabled: controlledOpen == null,
  });
  const dismiss = useDismiss(context, {
    ancestorScroll: dismissOnAncestorScroll,
  });
  const role = useRole(context);

  const interactions = useInteractions([click, dismiss, role]);

  return useMemo(
    () => ({
      openAnimation,
      closeAnimation,
      open,
      setOpen,
      ...interactions,
      ...data,
      modal,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
    }),
    [
      openAnimation,
      closeAnimation,
      open,
      setOpen,
      interactions,
      data,
      modal,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
    ],
  );
}

type ContextType =
  | (ReturnType<typeof usePopover> & {
      setLabelId: Dispatch<SetStateAction<string | undefined>>;
      setDescriptionId: Dispatch<SetStateAction<string | undefined>>;
    })
  | null;

const PopoverContext = createContext<ContextType>(null);

export const usePopoverContext = () => {
  const context = useContext(PopoverContext);

  if (context == null) {
    throw new Error('Popover components must be wrapped in <Popover />');
  }

  return context;
};

export function Popover({
  children,
  modal = false,
  ...restOptions
}: {
  children: ReactNode;
} & PopoverProps) {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const popover = usePopover({ modal, ...restOptions });
  return (
    <PopoverContext.Provider value={popover}>
      {children}
    </PopoverContext.Provider>
  );
}

export function PopoverTrigger({
  children,
  asChild = false,
  testId = 'ui-popover-trigget',
  ref: propRef,
  ...props
}: HTMLProps<HTMLElement> & PopoverTriggerProps & { ref?: Ref<HTMLElement> }) {
  const context = usePopoverContext();
  const childrenRef =
    asChild && isValidElement(children)
      ? (children.props as { ref?: Ref<HTMLElement> }).ref
      : null;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && isValidElement(children)) {
    const child = children as ReactElement;
    const childProps = child.props as HTMLAttributes<HTMLElement>;

    return cloneElement(child, {
      // eslint-disable-next-line react-hooks/refs
      ...context.getReferenceProps({
        ...(props as HTMLAttributes<HTMLElement>),
        ...childProps,
        ref,
        'data-state': context.open ? 'open' : 'closed',
        'data-testid': testId,
      } as HTMLAttributes<HTMLElement>),
    });
  }

  return (
    <Button
      ref={ref}
      type="button"
      data-state={context.open ? 'open' : 'closed'}
      data-testid={testId}
      {...context.getReferenceProps(props)}
    >
      {children}
    </Button>
  );
}

export function PopoverContent({
  style,
  className,
  testId = 'ui-popover-content',
  initialFocus,
  ref: propRef,
  ...props
}: HTMLProps<HTMLDivElement> &
  BaseComponentProps & {
    initialFocus?: number | RefObject<HTMLElement>;
    ref?: Ref<HTMLDivElement>;
  }) {
  const { context: floatingContext, ...context } = usePopoverContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(
    floatingContext,
    {
      duration: {
        open: context.openAnimation === undefined ? undefined : 300,
        close: context.closeAnimation === undefined ? undefined : 200,
      },
      open:
        context.openAnimation === undefined
          ? undefined
          : {
              animationName: context.openAnimation,
              animationDuration: '0.3s',
              animationTimingFunction: 'ease',
              animationFillMode: 'forwards',
            },
      close:
        context.closeAnimation === undefined
          ? undefined
          : {
              animationName: context.closeAnimation,
              animationDuration: '0.2s',
              animationTimingFunction: 'ease',
              animationFillMode: 'forwards',
            },
      common: ({ side }) => ({
        transformOrigin: {
          top: 'bottom',
          bottom: 'top',
          left: 'right',
          right: 'left',
        }[side],
      }),
    },
  );

  if (!isMounted) return null;

  return (
    <FloatingPortal>
      <FloatingFocusManager
        context={floatingContext}
        modal={context.modal}
        initialFocus={initialFocus}
      >
        <div
          ref={ref}
          style={context.floatingStyles}
          className={styles['floating']}
        >
          <div
            style={{ ...transitionStyles }}
            className={styles['popover']}
            data-testid={testId}
            aria-labelledby={context.labelId}
            aria-describedby={context.descriptionId}
            {...context.getFloatingProps(props)}
          >
            <div
              style={{ ...style }}
              className={classnames(styles['content'], className)}
            >
              {props.children}
            </div>
          </div>
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}
